import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
  Row,
  Col,
} from "reactstrap";
import Select from "react-select";
import toast from "react-hot-toast";
import ResponsiveDataManagement from "../../../Components/Common/ResponsiveManagementLayout";
import AccountingSettingService from "../../../services/AccountingServices/AccountingSettingService";
import LedgerTypeService from "../../../services/AccountingServices/LedgerTypeService";
import TableSearch from "../../../Components/Common/TableSearch";

const AutoLedgerSettings = () => {
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [ledgerTypes, setLedgerTypes] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Form State
  const [formData, setFormData] = useState({
    ledgerTypeId: "",
    nameTemplate: "",
    codeTemplate: "",
    isEnabled: true,
    description: "",
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [rulesData, typesData] = await Promise.all([
        AccountingSettingService.getAutoLedgerSettings(),
        LedgerTypeService.getList(),
      ]);
      
      setRules(rulesData || []);
      
      const types = Array.isArray(typesData) ? typesData : typesData?.items || [];
      const formattedTypes = types.map((t) => ({
        value: t.id,
        label: `${t.name} (${t.category})`,
      }));
      setLedgerTypes(formattedTypes);
    } catch (error) {
      console.error("Error fetching auto ledger data:", error);
      toast.error("Failed to load settings data");
    } finally {
      setLoading(false);
    }
  };

  const toggleModal = (rule = null) => {
    if (rule) {
      setSelectedRule(rule);
      setFormData({
        ledgerTypeId: rule.ledgerTypeId,
        nameTemplate: rule.nameTemplate,
        codeTemplate: rule.codeTemplate,
        isEnabled: rule.isEnabled,
        description: rule.description || "",
      });
    } else {
      setSelectedRule(null);
      setFormData({
        ledgerTypeId: "",
        nameTemplate: "",
        codeTemplate: "",
        isEnabled: true,
        description: "",
      });
    }
    setIsModalOpen(!isModalOpen);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSelectChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      ledgerTypeId: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedRule) return;

    try {
      setUpdating(true);
      await AccountingSettingService.updateAutoLedgerRule(selectedRule.id, formData);
      toast.success(`Successfully updated ${selectedRule.entityType} rule`);
      fetchInitialData();
      toggleModal();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update rule");
    } finally {
      setUpdating(false);
    }
  };

  const columns = [
    {
      name: "Entity Type",
      selector: (row) => row.entityType,
      sortable: true,
      cell: (row) => <span className="fw-bold">{row.entityType}</span>,
    },
    {
      name: "Ledger Type",
      selector: (row) => row.ledgerTypeName,
      sortable: true,
      cell: (row) => <span className="text-muted small">{row.ledgerTypeName}</span>,
    },
    {
      name: "Name Template",
      selector: (row) => row.nameTemplate,
      sortable: true,
      cell: (row) => <span className="text-muted small">{row.nameTemplate}</span>,
    },
    {
      name: "Code Template",
      selector: (row) => row.codeTemplate,
      sortable: true,
      cell: (row) => <code className="bg-light px-2 py-1 rounded text-dark small">{row.codeTemplate}</code>,
    },
    {
      name: "Status",
      selector: (row) => row.isEnabled,
      sortable: true,
      center: true,
      cell: (row) => (
        <span
  className={`badge rounded-pill ${
    row.isEnabled ? "bg-success" : "bg-danger"
  }`}
>
  {row.isEnabled ? "Enabled" : "Disabled"}
</span>
      ),
    },
  ];

  const stats = [
    {
      title: "Total Rules",
      value: rules.length,
      color: "#4A90E2",
      icon: "ri-list-settings-line",
    },
    {
      title: "Enabled",
      value: rules.filter(r => r.isEnabled).length,
      color: "#27AE60",
      icon: "ri-checkbox-circle-line",
    },
    {
      title: "Disabled",
      value: rules.filter(r => !r.isEnabled).length,
      color: "#E74C3C",
      icon: "ri-close-circle-line",
    },
  ];

  const filteredRules = useMemo(() => {
    if (!searchTerm) return rules;
    const lowerSearch = searchTerm.toLowerCase();
    return rules.filter(
      (r) =>
        r.entityType?.toLowerCase().includes(lowerSearch) ||
        r.ledgerTypeName?.toLowerCase().includes(lowerSearch) ||
        r.nameTemplate?.toLowerCase().includes(lowerSearch) ||
        r.codeTemplate?.toLowerCase().includes(lowerSearch)
    );
  }, [rules, searchTerm]);

  return (
    <div className="page-content p-0">
      <ResponsiveDataManagement
        title="Auto Ledger Settings"
        pageTitle="Accounting Setup"
        mobileCardTitle={null}
        data={filteredRules}
        columns={columns}
        stats={stats}
        onEdit={toggleModal}
        loading={loading}
        searchKeys={["entityType", "ledgerTypeName", "nameTemplate", "codeTemplate"]}
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        showSearch={false}
        emptyStateMessage="No auto-ledger rules found"
        customHeaderContent={
          <Row className="mb-3 align-items-center">
            <Col lg={3}>
              <h5 className="mb-1 fw-bold text-dark">Rule List</h5>
              <p className="text-muted small mb-0">Automatic ledger creation rules</p>
            </Col>
            <Col md={9}>
              <div className="d-flex justify-content-end">
                <div style={{ flex: '1 1 auto', minWidth: '150px', maxWidth: '300px' }}>
                  <TableSearch
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Search rules..."
                  />
                </div>
              </div>
            </Col>
          </Row>
        }
      />

      <Modal isOpen={isModalOpen} toggle={() => toggleModal()} size="lg" centered>
        <ModalHeader toggle={() => toggleModal()} className="pb-2 border-bottom-0">
          <span className="fw-medium text-dark">Edit Rule: </span>
          <span className="text-primary fw-medium">{selectedRule?.entityType}</span>
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody className="py-2">
            <Row className="g-3">
              <Col md={8}>
                <FormGroup className="mb-2">
                  <Label className="fw-semibold small text-muted text-uppercase mb-1">Ledger Type</Label>
                  <Select
                    options={ledgerTypes}
                    value={ledgerTypes.find(opt => opt.value === formData.ledgerTypeId)}
                    onChange={handleSelectChange}
                    placeholder="Select Ledger Type..."
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </FormGroup>
              </Col>
              <Col md={4} className="d-flex align-items-end">
                <FormGroup className="mb-2 pb-2">
                  <div className="form-check form-switch ps-5">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="isEnabled"
                      name="isEnabled"
                      checked={formData.isEnabled}
                      onChange={handleInputChange}
                    />
                    <Label className="form-check-label fw-semibold" htmlFor="isEnabled">
                      Status Enabled
                    </Label>
                  </div>
                </FormGroup>
              </Col>
            </Row>

            <Row className="g-3">
              <Col md={6}>
                <FormGroup className="mb-2">
                  <Label className="fw-semibold small text-muted text-uppercase mb-1">Name Template</Label>
                  <Input
                    type="text"
                    name="nameTemplate"
                    value={formData.nameTemplate}
                    onChange={handleInputChange}
                    placeholder="e.g. Accounts Receivable - {{EntityName}}"
                    required
                  />
                  <small className="text-muted">Use <code>{`{{EntityName}}`}</code> as placeholder.</small>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mb-2">
                  <Label className="fw-semibold small text-muted text-uppercase mb-1">Code Template</Label>
                  <Input
                    type="text"
                    name="codeTemplate"
                    value={formData.codeTemplate}
                    onChange={handleInputChange}
                    placeholder="e.g. AR-{{EntityId}}"
                    required
                  />
                  <small className="text-muted">Use <code>{`{{EntityId}}`}</code> as placeholder.</small>
                </FormGroup>
              </Col>
            </Row>

            <Row className="g-3">
              <Col lg={12}>
                <FormGroup className="mb-0">
                  <Label className="fw-semibold small text-muted text-uppercase mb-1">Description</Label>
                  <Input
                    type="textarea"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="2"
                    placeholder="Optional description..."
                  />
                </FormGroup>
              </Col>
            </Row>

            <div className="alert alert-soft-info mt-3 mb-0 py-2" style={{ borderLeft: "3px solid #0ab39c", fontSize: "0.85rem" }}>
              <i className="ri-information-line me-2 align-middle fs-16"></i>
              <strong>Tip:</strong> These templates are applied whenever a new {selectedRule?.entityType.toLowerCase()} is created in the system.
            </div>
          </ModalBody>
          <ModalFooter className="border-top-0 pt-0">
            <Button color="light" type="button" size="sm" onClick={() => toggleModal()}>Cancel</Button>
            <Button color="primary" type="submit" size="sm" disabled={updating}>
              {updating ? <Spinner size="sm" /> : "Update Rule"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default AutoLedgerSettings;
